import React, { useCallback, useRef } from 'react'
import {
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  DataGrid,
  DataGridRef,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Lookup,
  Paging,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
} from 'devextreme-react/data-grid'
import notify from 'devextreme/ui/notify'
import ODataStore from 'devextreme/data/odata/store'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { useScreenSize } from '@/themes/media-query'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import DocumentHead from '@/components/document-head/DocumentHead'

export const Agent = () => {
  const token = useTokenRefresh()
  const dataGridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()

  // Create data source for the agent grid
  const agentDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/agente`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['commerciale_qs'],
    requireTotalCount: true,
  }

  const commercialeQsDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/user`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    select: ['id', 'fullname'],
    sort: [{ selector: 'fullname', desc: false }],
  }

  const handleSuccess = useCallback((operation: string) => {
    notify(
      {
        message: `Operation successful: ${operation}`,
        type: 'success',
        displayTime: 3000,
      },
      {
        position: 'bottom center',
        direction: 'up-push',
      },
    )
  }, [])

  return (
    <div className="management-grid content-block">
      <h2>Commerciali</h2>

      <DataGrid
        dataSource={agentDataSource}
        height="75vh"
        className={currentScreenSize.isSmall ? 'grid-height-small' : 'grid-height-default'}
        noDataText="Nessun commerciale trovato"
        showBorders={false}
        remoteOperations={{
          filtering: true,
          sorting: true,
          paging: true,
          grouping: true,
          groupPaging: true,
          summary: true,
        }}
        showColumnLines={true}
        focusedRowEnabled={false}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
        allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
        allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
        rowAlternationEnabled={true}
        wordWrapEnabled={true}
        groupPanel={{
          visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
          emptyPanelText: 'Trascina qui una colonna per raggruppare',
        }}
        onRowInserted={(e) => handleSuccess('Created')}
        onRowUpdated={(e) => handleSuccess('Updated')}
        ref={dataGridRef}
      >
        <Editing mode="batch" allowAdding={true} allowUpdating={true} allowDeleting={false} useIcons={true} />

        <StateStoring enabled={true} type="localStorage" storageKey="agents-grid" savingTimeout={50} />

        <HeaderFilter visible />
        <FilterPanel visible />
        <FilterRow visible />
        <SearchPanel visible width={currentScreenSize.isXSmall ? 200 : 250} />
        <Scrolling mode="virtual" />
        <Sorting mode="multiple" />
        <Export enabled />
        <ColumnChooser enabled>
          <ColumnChooserSearch enabled />
        </ColumnChooser>

        <Paging defaultPageSize={20} enabled />

        <Column dataField="id" caption="ID" dataType="number" width={80} allowEditing={false} visible={false} />

        <Column
          dataField="commerciale_qs.id"
          caption="Commerciale QS"
          dataType="number"
          visible={true}
          allowSorting={true}
          allowEditing={false}
          hidingPriority={1}
        >
          <Lookup dataSource={commercialeQsDataSource} valueExpr="id" displayExpr="fullname" />
        </Column>

        <Column dataField="note" caption="Note" dataType="string" visible={true} allowSorting={true} />

        <Column
          dataField="attivo"
          caption="Attivo"
          dataType="boolean"
          visible={true}
          allowSorting={true}
          hidingPriority={2}
        />
      </DataGrid>
    </div>
  )
}
